import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BadgeCheck, Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import MPlayShell, { Avatar, VCard, fmtViews } from "./VidShared";
import { useAuth } from "../../context/AuthContext";

export default function ChannelPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/vid/channel/${slug}`).then((r) => {
      setData(r.data);
      setFollowers(r.data.channel.followers);
      if (user) api.get(`/vid/creators/${r.data.channel.id}/follow-status`).then((x) => setFollowing(x.data.following)).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [slug, user]);

  const toggleFollow = async () => {
    if (!user) { navigate(`/mkt/auth?mode=login&returnTo=${encodeURIComponent(`/channel/${slug}`)}`); return; }
    try {
      const { data: d } = await api.post(`/vid/creators/${data.channel.id}/follow`);
      setFollowing(d.following); setFollowers(d.followers);
      toast.success(d.following ? `Following ${data.channel.name}` : "Unfollowed");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <MPlayShell>
      <div className="px-3 sm:px-6 pt-6 max-w-[1200px] mx-auto" data-testid="channel-page">
        {notFound ? (
          <div className="flex flex-col items-center justify-center py-32 gap-3" data-testid="channel-notfound">
            <Play className="h-12 w-12 text-emerald-400" />
            <p className="text-white font-extrabold text-xl">Channel not found</p>
            <Link to="/videos" className="text-emerald-400 text-sm font-bold">Back to videos</Link>
          </div>
        ) : !data ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 py-4" data-testid="channel-header">
              <Avatar name={data.channel.name} src={data.channel.avatar} size={96} />
              <div className="min-w-0 flex-1">
                <h1 className="text-white font-extrabold text-2xl flex items-center gap-2 truncate" data-testid="channel-name">
                  {data.channel.name} <BadgeCheck className="h-5 w-5 text-emerald-400 shrink-0" />
                </h1>
                <p className="text-white/50 text-[13px] mt-1" data-testid="channel-stats">
                  @{data.channel.slug} · {fmtViews(followers)} followers · {data.channel.video_count} videos
                </p>
                {data.channel.bio && <p className="text-white/70 text-[13px] mt-2 line-clamp-2 max-w-xl" data-testid="channel-bio">{data.channel.bio}</p>}
                {user?.id !== data.channel.id && (
                  <button onClick={toggleFollow} data-testid="channel-follow"
                    className={`mt-3 h-9 px-5 rounded-full text-[13px] font-extrabold transition-all ${following ? "bg-white/[0.1] text-white hover:bg-white/[0.16]" : "mp-green"}`}>
                    {following ? "Following" : "Follow"}
                  </button>
                )}
              </div>
            </div>
            <div className="border-t border-white/[0.07] mt-2 pt-6">
              <h2 className="text-white font-extrabold text-lg mb-4">Videos</h2>
              {data.videos.length === 0 ? (
                <p className="text-white/50 text-sm py-16 text-center" data-testid="channel-empty">No videos yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-x-4 gap-y-8 pb-10" data-testid="channel-videos">
                  {data.videos.map((v) => <VCard key={v.id} v={v} />)}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </MPlayShell>
  );
}
